import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { BookService } from "../../../services/bookService";
import { useLanguage } from "@/i18n/LanguageContext";
import {
  Book,
  BookSection,
  CreateBookSection,
  ViewerOption,
  isVisibleToStudents,
} from "../../schemas/book-schemas";
import type { SetReaderVisible } from "../../schemas/book-schemas";

// Import the new components
import { BookHeader } from "./book-header";
import { BookPreviewData } from "../preview/BookPreview";
import { BookNavigation } from "./book-navigation";
import { ModuleCard } from "./module-card";
import QuestionModal from "./question-modal";
import { Question } from "../../schemas/schemas";
import { BookImportModal } from "./book-import-modal";
import toast from "react-hot-toast";

// Derive a file_path from a full file_url by stripping scheme, host and port.
// Examples:
//   https://example.com:8080/api/file/123?token=abc -> /api/file/123?token=abc
//   /api/file/123?token=abc -> /api/file/123?token=abc
const getFilePathFromUrl = (url: string | undefined | null): string => {
  if (!url || typeof url !== "string") {
    return "";
  }

  try {
    const parsed = new URL(url, window.location.origin);
    return `${parsed.pathname}${parsed.search || ""}`;
  } catch {
    // If it's already a relative path or URL constructor fails,
    // strip protocol and host manually as a fallback.
    return url.replace(/^https?:\/\/[^/]+/i, "");
  }
};

// Content Type Selection Modal Component
interface ContentTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (contentType: string) => void;
}

const ContentTypeModal = ({ isOpen, onClose, onSelect }: ContentTypeModalProps) => {
  const contentTypes = [
    { id: 'container', name: 'Container Section', description: 'Phần chứa có thể có nhiều phần con', icon: '📁' },
    { id: 'html', name: 'HTML Content', description: 'Nội dung HTML tùy chỉnh', icon: '📝' },
    { id: 'pdf', name: 'PDF Document', description: 'Tài liệu PDF', icon: '📄' },
    { id: 'image', name: 'Image', description: 'Hình ảnh', icon: '🖼️' },
    { id: 'video', name: 'Video', description: 'Video file', icon: '🎥' },
    { id: 'audio', name: 'Audio', description: 'Audio file', icon: '🎵' },
    { id: 'scorm', name: 'SCORM Package', description: 'Gói SCORM', icon: '📦' },
    { id: 'html_package', name: 'Học liệu HTML', description: 'Một file trang .html, không phải gói SCORM', icon: '🌐' },
    { id: 'pptx', name: 'PowerPoint', description: 'Bản trình bày PowerPoint', icon: '📊' },
    { id: 'quiz', name: 'Quiz', description: 'Bài kiểm tra', icon: '❓' },
    { id: 'embed', name: 'Embed Content', description: 'Nội dung nhúng', icon: '🔗' },
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4">
        <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
          Chọn loại nội dung
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Chọn loại nội dung cho phần mới. Bạn có thể thay đổi sau này.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {contentTypes.map((type) => (
            <button
              key={type.id}
              onClick={() => onSelect(type.id)}
              className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left"
            >
              <div className="flex items-center space-x-3">
                <span className="text-2xl">{type.icon}</span>
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-gray-100">
                    {type.name}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {type.description}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
        
        <div className="flex justify-end space-x-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
          >
            Hủy
          </button>
        </div>
      </div>
    </div>
  );
};

// Use simplified book schemas
type BookData = Book;
type Chapter = BookSection;
type Section = BookSection;

interface BookComposerProps {
  bookId: string;
  itemId?: string;
  itemType?: string;
}

// Depth-first lookup of a section anywhere in the chapter tree
const findSectionById = (sections: any[], sectionId: string): any | null => {
  for (const section of sections) {
    if (section.id === sectionId) return section;
    if (section.children?.length) {
      const found = findSectionById(section.children, sectionId);
      if (found) return found;
    }
  }
  return null;
};

const VIEWER_OPTION_ERRORS: Record<ViewerOption, string> = {
  allow_print: "Có lỗi xảy ra khi cập nhật quyền in file!",
  allow_download: "Có lỗi xảy ra khi cập nhật quyền tải file!",
  visible_to_students: "Có lỗi xảy ra khi cập nhật hiển thị với học sinh!",
};

const defaultBookData: BookData = {
  id: "",
  title: "",
  description: "",
  category: "",
  status: "draft",
  contents: [],
};

export default function BookComposer({ bookId }: BookComposerProps) {
  const { t } = useLanguage();

  // State for the book structure
  const [bookData, setBookData] = useState<BookData>(defaultBookData);

  // Handler chạy sau `await` giữ `bookData` của lần render bắt đầu nó; ref luôn
  // trỏ bản mới nhất để hai thao tác liên tiếp không đọc cùng một bản cũ.
  const bookDataRef = useRef(bookData);
  bookDataRef.current = bookData;
  const [loading, setLoading] = useState(true);
  const [isAddingSection, setIsAddingSection] = useState<string | null>(null);
  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [currentQuizSectionId, setCurrentQuizSectionId] = useState<string | null>(null);
  const [isContentTypeModalOpen, setIsContentTypeModalOpen] = useState(false);
  const [pendingChapterId, setPendingChapterId] = useState<string | null>(null);
  const [pendingContainerId, setPendingContainerId] = useState<string | null>(null);
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importType, setImportType] = useState<'json' | 'dlow'>('json');

  // Handle navigation item selection
  const handleItemSelect = (itemId: string) => {
    setSelectedSectionId(itemId);
  };

  // Scroll to selected section
  useEffect(() => {
    if (selectedSectionId) {
      const element = document.querySelector(`[data-section-id="${selectedSectionId}"]`);
      if (element) {
        element.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        
        // Add highlight effect
        element.classList.add('section-highlight');
        
        // Remove highlight after 2 seconds
        setTimeout(() => {
          element.classList.remove('section-highlight');
        }, 2000);
      }
    }
  }, [selectedSectionId]);

  // Transform bookData to BookPreviewData
  const transformToPreviewData = (book: BookData): BookPreviewData => {
    return {
      id: book.id,
      title: book.title,
      description: book.description || '',
      cover_image: book.cover_image_url,
      author: book.author,
      category: book.category,
      status: book.status as 'draft' | 'published' | 'archived',
      created_at: book.createdAt,
      updated_at: book.updatedAt,
      contents: book.contents.map((chapter, index) => ({
        id: chapter.id || `chapter-${index}`,
        title: chapter.title || `Chương ${index + 1}`,
        order: chapter.order || index,
        duration: chapter.estimates?.reading_time_min || chapter.duration || 0,
        start_day_offset: chapter.start_day_offset,
        start_day_offset_relative_to: chapter.start_day_offset_relative_to,
        start_day_offset_reference_id: chapter.start_day_offset_reference_id,
        children: chapter.children?.map((section, sectionIndex) => ({
          id: section.id || `section-${sectionIndex}`,
          title: section.title || `Phần ${sectionIndex + 1}`,
          order: section.order || sectionIndex,
          type: section.type as any,
          duration: section.estimates?.reading_time_min || section.duration || 0,
          start_day_offset: section.start_day_offset,
          start_day_offset_relative_to: section.start_day_offset_relative_to,
          start_day_offset_reference_id: section.start_day_offset_reference_id,
          file_id: section.file_id,
          file: section.file,
          content_block: section.content_block,
          children: section.children ? section.children.map((child, childIndex) => ({
            id: child.id || `child-${childIndex}`,
            title: child.title || `Nội dung ${childIndex + 1}`,
            order: child.order || childIndex,
            type: child.type as any,
            duration: child.estimates?.reading_time_min || child.duration || 0,
            start_day_offset: child.start_day_offset,
            start_day_offset_relative_to: child.start_day_offset_relative_to,
            start_day_offset_reference_id: child.start_day_offset_reference_id,
            file_id: child.file_id,
            file: child.file,
            content_block: child.content_block,
            children: child.children
          })) : undefined
        })) || []
      }))
    };
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // Load book data from API
        const response = await BookService.getBookDetails(bookId);
        const data = response.data || response;
        setBookData(data);

        // Question bank not needed for books
      } catch (error) {
        console.error("Failed to load book data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [bookId]);

  // Function to reload book data from server
  const reloadBookData = async () => {
    try {
      setLoading(true);
      const response = await BookService.getBookDetails(bookId);
      const data = response.data || response;
      setBookData(data);
      
    } catch (error) {
      console.error("Failed to reload book data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Book-specific state management

  // Function to create content_block based on content_type
  const createContentBlock = (contentType: string) => {
    switch (contentType) {
      case 'container':
        return {
          content_type: "html" as const, // Container sections can have HTML descriptions
          payload: {
            html: "<p>Phần chứa này có thể chứa nhiều phần con khác.</p>"
          }
        };
      case 'html':
        return {
          content_type: "html" as const,
          payload: {
            html: "<p>Nhập nội dung HTML của bạn tại đây...</p>"
          }
        };
      case 'pdf':
        return {
          content_type: "pdf" as const,
          payload: {
            file_url: "",
            file_path: "",
            page_from: 1,
            page_to: 1,
            viewer: {
              allow_download: false,
              allow_print: false,
              start_page: 1
            }
          }
        };
      case 'image':
        return {
          content_type: "image" as const,
          payload: {
            file_url: "",
            file_path: "",
            alt_text: "",
            caption: ""
          }
        };
      case 'video':
        return {
          content_type: "video" as const,
          payload: {
            file_url: "",
            file_path: "",
            poster: "",
            transcript_url: ""
          }
        };
      case 'audio':
        return {
          content_type: "audio" as const,
          payload: {
            file_url: "",
            file_path: "",
            caption: ""
          }
        };
      case 'scorm':
        return {
          content_type: "scorm" as const,
          payload: {
            file_url: "",
            file_path: "",
            version: "SCORM_1_2",
            reporting: {
              commit_interval_sec: 10
            },
            completion_mapping: {
              on: "cmi.core.lesson_status",
              completed_values: ["completed", "passed"]
            },
            offline_access: {
              enabled: true,
              download_on_import: true,
              local_file_required: true
            }
          }
        };
      case 'html_package':
        return {
          content_type: "html_package" as const,
          payload: {
            file_url: "",
            file_path: ""
          }
        };
      case 'pptx':
        return {
          content_type: "pptx" as const,
          payload: {
            file_url: "",
            file_path: "",
            viewer: {
              web_player: "office",
              allow_download: true
            }
          }
        };
      case 'quiz':
        return {
          content_type: "quiz" as const,
          payload: {
            questions: [],
            time_limit_minutes: 30,
            passing_score: 70,
            allow_retake: true,
            show_correct_answers: true
          }
        };
      case 'embed':
        return {
          content_type: "embed" as const,
          payload: {
            url: "",
            title: "",
            description: ""
          }
        };
      default:
        return {
          content_type: "html" as const,
          payload: {
            html: "<p>Nhập nội dung HTML của bạn tại đây...</p>"
          }
        };
    }
  };

  // Handler for adding new chapter
  const handleAddChapter = async () => {
    try {
      const newChapter: CreateBookSection = {
        title: `Chương ${(bookData.contents || []).length + 1}`,
        order: (bookData.contents || []).length + 1,
        type: 'chapter',
        book_id: bookId, // Add book_id to associate chapter with book
        children: [],
        // Add default access control for chapters
        access: {
          visibility: "public",
          is_free_preview: true,
          unlock_conditions: []
        }
      };

      

      // Create chapter via API
      const response = await BookService.createChapter(bookId, newChapter);
      
      
      
      if (response.success) {
        // Ensure we have the complete chapter data from the response
        const createdChapter = response.data;
        
        if (!createdChapter || !createdChapter.id) {
          console.error("API response missing chapter data:", createdChapter);
          throw new Error("Invalid chapter data received from API");
        }

        // Update local state with the created chapter
        setBookData((prevBook) => {
          const updatedBook = { ...prevBook };
          
          // Ensure contents array exists
          if (!updatedBook.contents) {
            updatedBook.contents = [];
          }
          
          // Add the new chapter with complete data
          const chapterToAdd = {
            ...newChapter,
            ...createdChapter, // Merge with API response data
            id: createdChapter.id, // Ensure ID is set
            children: createdChapter.children || [], // Ensure children array exists
          };
          
          updatedBook.contents = [
            ...updatedBook.contents,
            chapterToAdd,
          ];
          
          
          
          return updatedBook;
        });
      } else {
        throw new Error(response.message || "Failed to create chapter");
      }
    } catch (error) {
      console.error("Failed to add chapter:", error);
      toast.error("Có lỗi xảy ra khi thêm chương!");
      
      // If there was an error, try to reload data from server to ensure consistency
      reloadBookData();
    }
  };

  // Handler for adding new section to a chapter - now shows content type modal
  const handleAddSection = (chapterId: string) => {
    setPendingChapterId(chapterId);
    setPendingContainerId(null); // Ensure we're adding to chapter, not container
    setIsContentTypeModalOpen(true);
  };

  // Handler for adding new subsection to a container section
  const handleAddSubSection = (containerId: string) => {
    setPendingContainerId(containerId);
    setPendingChapterId(null); // Ensure we're adding to container, not chapter
    setIsContentTypeModalOpen(true);
  };

  // Handler for content type selection
  const handleContentTypeSelect = async (contentType: string) => {
    if (!pendingChapterId && !pendingContainerId) {
      return;
    }

    try {
      // Determine which parent we're adding to
      const parentId = pendingChapterId || pendingContainerId;
      const isAddingToContainer = !!pendingContainerId;
      
      // Prevent duplicate calls
      if (isAddingSection === parentId) {
        return;
      }

      setIsAddingSection(parentId!);
      setIsContentTypeModalOpen(false);

      let sectionCount = 0;
      let parentChapterId = "";

      if (isAddingToContainer) {
        // Adding to container section - find the container within chapters
        let containerSection = null;
        for (const chapter of bookData.contents || []) {
          for (const section of chapter.children || []) {
            if (section.id === pendingContainerId) {
              containerSection = section;
              parentChapterId = chapter.id || "";
              break;
            }
          }
          if (containerSection) break;
        }
        
        if (!containerSection) {
          throw new Error("Container section not found");
        }
        
        sectionCount = containerSection.children?.length || 0;
      } else {
        // Adding to chapter
        const chapter = (bookData.contents || []).find((c) => c.id === pendingChapterId);
        if (!chapter) {
          throw new Error("Chapter not found");
        }
        sectionCount = chapter.children?.length || 0;
        parentChapterId = pendingChapterId!;
      }

      const newSection: CreateBookSection = {
        title: contentType === "container" ? `Phần ${sectionCount + 1}` : 
               contentType === "quiz" ? `Bài kiểm tra ${sectionCount + 1}` : `Phần ${sectionCount + 1}`,
        order: sectionCount + 1,
        type: contentType === "container" ? "container" :
              contentType === "quiz" ? "quiz" : "content",
        book_id: bookId, // Add book_id to associate section with book
        parent_section_id: isAddingToContainer ? pendingContainerId! : pendingChapterId!, // Add parent_section_id
        content: {},
        // Add default access control and progress rules
        access: {
          visibility: "public",
          is_free_preview: true,
          unlock_conditions: []
        },
        progress_rules: {
          completion_type: contentType === "quiz" ? "quiz_pass" : "viewed",
          passing_score: contentType === "quiz" ? 70 : undefined
        },
        estimates: {
          reading_time_min: contentType === "quiz" ? 10 : 5
        },
        // Add content_block based on selected content type
        content_block: createContentBlock(contentType),
        // Initialize children array for container sections
        children: contentType === "container" ? [] : undefined
      };



      // Create section via API
      const response = await BookService.createChildSection(
        isAddingToContainer ? pendingContainerId! : pendingChapterId!,
        newSection
      );
      

      
      if (response.success) {
        // Ensure we have the complete section data from the response
        const createdSection = response.data;
        
        if (!createdSection || !createdSection.id) {
          console.error("API response missing section data:", createdSection);
          throw new Error("Invalid section data received from API");
        }

        // Update local state with the created section
        setBookData((prevBook) => {
          const updatedBook = { ...prevBook };
          
          if (isAddingToContainer) {
            // Adding to container section
            for (const chapter of updatedBook.contents || []) {
              for (const section of chapter.children || []) {
                if (section.id === pendingContainerId) {
                  // Ensure children array exists
                  if (!section.children) {
                    section.children = [];
                  }
                  
                  // Add the new subsection with complete data
                  const sectionToAdd = {
                    ...newSection,
                    ...createdSection, // Merge with API response data
                    id: createdSection.id, // Ensure ID is set
                    // Ensure children array is preserved for container sections
                    children: contentType === "container" ? (createdSection.children || []) : undefined
                  };
                  
                  section.children = [
                    ...section.children,
                    sectionToAdd,
                  ];
                  return updatedBook;
                }
              }
            }
          } else {
            // Adding to chapter
            const chapterIndex = updatedBook.contents?.findIndex(
              (c) => c.id === pendingChapterId
            );

            if (chapterIndex !== undefined && chapterIndex >= 0) {
              const chapter = updatedBook.contents[chapterIndex];
              
              // Ensure children array exists
              if (!chapter.children) {
                chapter.children = [];
              }
              
              // Add the new section with complete data
              const sectionToAdd = {
                ...newSection,
                ...createdSection, // Merge with API response data
                id: createdSection.id, // Ensure ID is set
                // Ensure children array is preserved for container sections
                children: contentType === "container" ? (createdSection.children || []) : undefined
              };
              
              chapter.children = [
                ...chapter.children,
                sectionToAdd,
              ];
            }
          }

          return updatedBook;
        });
        

      } else {
        throw new Error(response.message || "Failed to create section");
      }

      // Reset the adding state after a short delay
      setTimeout(() => {
        setIsAddingSection(null);
        setPendingChapterId(null);
        setPendingContainerId(null);
      }, 1000);
    } catch (error) {
      console.error("Failed to add section:", error);
      toast.error("Có lỗi xảy ra khi thêm phần!");
      setIsAddingSection(null);
      setPendingChapterId(null);
      setPendingContainerId(null);
      
      // If there was an error, try to reload data from server to ensure consistency
      reloadBookData();
    }
  };

  // Handler for updating chapter
  const handleUpdateChapter = async (
    chapterId: string,
    updatedChapter: Partial<Chapter>
  ) => {
    try {
      // Include book_id in the updated chapter data to maintain association
      const chapterDataWithBookId = {
        ...updatedChapter,
        book_id: bookId,
      };

      const response = await BookService.updateSection(
        chapterId,
        chapterDataWithBookId
      );
      if (response.success) {
        // Update local state
        setBookData((prevBook) => {
          const updatedBook = { ...prevBook };

          for (let i = 0; i < (updatedBook.contents || []).length; i++) {
            if (updatedBook.contents[i].id === chapterId) {
              updatedBook.contents[i] = {
                ...updatedBook.contents[i],
                ...updatedChapter,
              };
              break;
            }
          }

          return updatedBook;
        });

      } else {
        throw new Error(response.message || "Failed to update chapter");
      }
    } catch (error) {
      console.error("Failed to update chapter:", error);
      toast.error("Có lỗi xảy ra khi cập nhật chương!");
    }
  };

  // Handler for updating section
  const handleUpdateSection = async (
    parentSectionId: string,
    sectionId: string,
    updatedSection: Partial<Section>
  ) => {
    try {
      // Include book_id in the updated section data to maintain association
      const sectionDataWithBookId = {
        ...updatedSection,
        book_id: bookId,
        parent_section_id: parentSectionId,
      };

      const response = await BookService.updateSection(
        sectionId,
        sectionDataWithBookId
      );
      if (response.success) {
        // Update local state
        setBookData((prevBook) => {
          const updatedBook = { ...prevBook };

          // Helper function to update section in nested structure
          const updateSectionInChapter = (sections: any[]): boolean => {
            for (let i = 0; i < sections.length; i++) {
              if (sections[i].id === sectionId) {
                sections[i] = {
                  ...sections[i],
                  ...updatedSection,
                  // Preserve the original ID (don't let it be overwritten with undefined)
                  id: sections[i].id,
                  // Preserve children array for container sections
                  children: sections[i].type === 'container' ? sections[i].children : updatedSection.children
                };
                return true;
              }
              // Check subsections (container children)
              if (sections[i].children && sections[i].children.length > 0) {
                if (updateSectionInChapter(sections[i].children)) {
                  return true;
                }
              }
            }
            return false;
          };

          for (const chapter of updatedBook.contents || []) {
            if (chapter.children) {
              updateSectionInChapter(chapter.children);
            }
          }

          return updatedBook;
        });

      } else {
        throw new Error(response.message || "Failed to update section");
      }
    } catch (error) {
      console.error("Failed to update section:", error);
      toast.error("Có lỗi xảy ra khi cập nhật phần!");
    }
  };

  // Handler for deleting chapter
  const handleDeleteChapter = async (chapterId: string) => {
    try {
      // Validate chapter ID
      if (!chapterId || chapterId === "undefined" || chapterId === "") {
        console.error("❌ Invalid chapter ID:", chapterId);
        console.error("ChapterId === undefined:", chapterId === undefined);
        console.error("ChapterId === null:", chapterId === null);
        console.error("ChapterId === 'undefined':", chapterId === "undefined");
        console.error("ChapterId === '':", chapterId === "");
        console.error("Boolean chapterId:", !!chapterId);
        toast.error("ID chương không hợp lệ!");
        return;
      }

      const response = await BookService.deleteSection(chapterId);
      
      if (response.success) {
        // Update local state
        setBookData((prevBook) => {
          const updatedBook = {
            ...prevBook,
            contents: (prevBook.contents || []).filter(
              (chapter) => chapter.id !== chapterId
            ),
          };
          return updatedBook;
        });
      } else {
        console.error("❌ API call failed:", response.message);
        throw new Error(response.message || "Failed to delete chapter");
      }
    } catch (error) {
      console.error("❌ Error in handleDeleteChapter:", error);
      toast.error("Có lỗi xảy ra khi xóa chương!");
      
      // If there was an error, try to reload data from server to ensure consistency
      reloadBookData();
    }
  };

  // Handler for deleting section
  const handleDeleteSection = async (chapterId: string, sectionId: string) => {
    try {
      // Validate section ID
      if (!sectionId || sectionId === "undefined" || sectionId === "") {
        
        return;
      }

      // Validate chapter ID
      if (!chapterId || chapterId === "undefined" || chapterId === "") {
       
        return;
      }
      console.log("sectionId", sectionId);
      const response = await BookService.deleteSection(sectionId);
      
      if (response.success) {
        // Update local state
        setBookData((prevBook) => {
          const updatedBook = { ...prevBook };

          for (const chapter of updatedBook.contents || []) {
            if (chapter.id === chapterId) {
              chapter.children = (chapter.children || []).filter(
                (section) => section.id !== sectionId
              );
              break;
            }
          }

          return updatedBook;
        });
      } else {
        console.error("❌ API call failed:", response.message);
        throw new Error(response.message || "Failed to delete section");
      }
    } catch (error) {
      console.error("❌ Error in handleDeleteSection:", error);
      toast.error("Có lỗi xảy ra khi xóa phần!");
      
      // If there was an error, try to reload data from server to ensure consistency
      reloadBookData();
    }
  };

  // Handler for content upload to sections (supports both file upload and URL input)
  const handleContentUpload = async (
    sectionId: string,
    file: File,
    contentType: string,
    fileData?: any,
    /** Which entry of a merged section's content_blocks is being replaced; undefined for a single content_block */
    blockIndex?: number
  ) => {
    try {
      if (!fileData) {
        throw new Error("No file data received");
      }

      // Use secure file URLs if available
      const fileUrl = fileData.view_url || fileData.url || fileData.download_url;
      if (!fileUrl) {
        throw new Error("No file URL available");
      }

      // Find the actual parent of this section (could be chapter or container section)
      let parentSectionId = "";
      let chapterId = "";
      
      const findSectionParent = (sections: any[], targetSectionId: string, parentId: string): { parentId: string; found: boolean } => {
        for (const section of sections) {
          if (section.id === targetSectionId) {
            return { parentId, found: true };
          }
          // Check nested sections (for container sections)
          if (section.children && section.children.length > 0) {
            const result = findSectionParent(section.children, targetSectionId, section.id);
            if (result.found) {
              return result;
            }
          }
        }
        return { parentId: "", found: false };
      };

      for (const chapter of bookData.contents || []) {
        if (chapter.children) {
          const result = findSectionParent(chapter.children, sectionId, chapter.id);
          if (result.found) {
            parentSectionId = result.parentId;
            chapterId = chapter.id || "";
            break;
          }
        }
      }

      if (!chapterId || !parentSectionId) {
        throw new Error("Parent section not found for section");
      }

      // Determine content type and create appropriate content_block
      let contentBlock: any = null;
      if (contentType === "scorm") {
        if (fileData.type === "url") {
          // SCORM URL
        contentBlock = {
          content_type: "scorm",
          payload: {
            file_id: fileData._id,
            file_url: fileUrl,
            file_path: getFilePathFromUrl(fileUrl),
            file_name: "SCORM URL",
            storage: fileData.storage || 'public',
            version: "SCORM_1_2",
            reporting: {
              commit_interval_sec: 10
            },
            completion_mapping: {
              on: "cmi.core.lesson_status",
              completed_values: ["completed", "passed"]
            },
            offline_access: {
              enabled: true,
              download_on_import: true,
              local_file_required: true
            }
          }
        };
        } else {
          // SCORM File
          // Ensure filename includes extension
          const scormFileName = fileData.original_name 
            ? (fileData.extension && !fileData.original_name.endsWith(`.${fileData.extension}`)
                ? `${fileData.original_name}.${fileData.extension}`
                : fileData.original_name)
            : "scorm-package.zip";
          
          contentBlock = {
            content_type: "scorm",
            payload: {
              file_id: fileData._id,
              file_url: fileUrl,
              file_path: getFilePathFromUrl(fileUrl),
              file_name: scormFileName,
              storage: fileData.storage || 'public',
              version: "SCORM_1_2",
              reporting: {
                commit_interval_sec: 10,
              },
              completion_mapping: {
                on: "cmi.core.lesson_status",
                completed_values: ["completed", "passed"],
              },
              offline_access: {
                enabled: true,
                download_on_import: true,
                local_file_required: true,
              },
            },
          };
        }
      } else if (contentType === "html_package") {
        // Server lưu file .html thành gói zip một trang, nên extension trả về
        // là zip; tên hiển thị giữ tên file admin chọn
        const packageFileName = fileData.original_name || "hoc-lieu.html";

        contentBlock = {
          content_type: "html_package",
          payload: {
            file_id: fileData._id,
            file_url: fileUrl,
            file_path: getFilePathFromUrl(fileUrl),
            file_name: packageFileName,
            storage: fileData.storage || 'public',
            // Trang trong gói server dựng lúc upload; reader mở đúng trang
            // này sau khi giải nén
            entry_path: fileData.options?.entry_path,
            encrypted: fileData.options?.encrypted === true,
          },
        };
      } else if (contentType === "pdf") {
        // Ensure filename includes extension
        const pdfFileName = fileData.original_name 
          ? (fileData.extension && !fileData.original_name.endsWith(`.${fileData.extension}`)
              ? `${fileData.original_name}.${fileData.extension}`
              : fileData.original_name)
          : "document.pdf";
        
        contentBlock = {
          content_type: "pdf",
          payload: {
            file_id: fileData._id,
            file_url: fileUrl,
            file_path: getFilePathFromUrl(fileUrl),
            file_name: pdfFileName,
            storage: fileData.storage || 'public',
            page_from: 1,
            page_to: 18,
            viewer: {
              allow_download: false,
              allow_print: false,
              start_page: 1,
            },
          },
        };
      } else if (contentType === "image") {
        // Ensure filename includes extension
        const imageFileName = fileData.original_name 
          ? (fileData.extension && !fileData.original_name.endsWith(`.${fileData.extension}`)
              ? `${fileData.original_name}.${fileData.extension}`
              : fileData.original_name)
          : `image.${fileData.extension || 'jpg'}`;
        
        contentBlock = {
          content_type: "image",
          payload: {
            file_id: fileData._id,
            file_url: fileUrl,
            file_path: getFilePathFromUrl(fileUrl),
            file_name: imageFileName,
            storage: fileData.storage || 'public',
            alt_text: imageFileName,
            caption: ""
          }
        };
      } else if (contentType === "video") {
        // Ensure filename includes extension
        const videoFileName = fileData.original_name 
          ? (fileData.extension && !fileData.original_name.endsWith(`.${fileData.extension}`)
              ? `${fileData.original_name}.${fileData.extension}`
              : fileData.original_name)
          : `video.${fileData.extension || 'mp4'}`;
        
        contentBlock = {
          content_type: "video",
          payload: {
            file_id: fileData._id,
            file_url: fileUrl,
            file_path: getFilePathFromUrl(fileUrl),
            file_name: videoFileName,
            file_size: parseInt(fileData.size, 10) || 0,
            storage: fileData.storage || 'public',
            video_metadata: fileData.video_metadata || undefined,
            // No poster or transcript_url: deriving them from the video URL
            // invents a file nobody uploaded. On a secure URL there is no
            // extension to swap, so both used to come out as the video's own
            // URL, which sits behind auth and only ever answered 401.
          }
        };
      } else if (contentType === "audio") {
        // Ensure filename includes extension
        const audioFileName = fileData.original_name 
          ? (fileData.extension && !fileData.original_name.endsWith(`.${fileData.extension}`)
              ? `${fileData.original_name}.${fileData.extension}`
              : fileData.original_name)
          : `audio.${fileData.extension || 'mp3'}`;
        
        contentBlock = {
          content_type: "audio",
          payload: {
            file_id: fileData._id,
            file_url: fileUrl,
            file_path: getFilePathFromUrl(fileUrl),
            file_name: audioFileName,
            storage: fileData.storage || 'public',
            caption: ""
          }
        };
      } else if (contentType === "pptx") {
        // Ensure filename includes extension
        const pptxFileName = fileData.original_name 
          ? (fileData.extension && !fileData.original_name.endsWith(`.${fileData.extension}`)
              ? `${fileData.original_name}.${fileData.extension}`
              : fileData.original_name)
          : "presentation.pptx";
        
        contentBlock = {
          content_type: "pptx",
          payload: {
            file_id: fileData._id,
            file_url: fileUrl,
            file_path: getFilePathFromUrl(fileUrl),
            file_name: pptxFileName,
            storage: fileData.storage || 'public',
            viewer: {
              web_player: "office",
              allow_download: true
            }
          }
        };
      } else {
        // Default to HTML content
        contentBlock = {
          content_type: "html",
          payload: {
            html: `<p>Nội dung từ file: ${fileData.original_name}</p>`
          }
        };
      }

      // A merged section keeps its blocks in content_blocks: replacing one file
      // must patch that entry, not overwrite the section's single content_block.
      const targetSection = findSectionById(bookData.contents || [], sectionId);
      const mergedBlocks =
        blockIndex !== undefined && Array.isArray(targetSection?.content_blocks)
          ? targetSection.content_blocks
          : null;
      const replacedBlock = mergedBlocks ? mergedBlocks[blockIndex!] : targetSection?.content_block;

      // Replacing the file of a block hidden from students must not silently
      // publish the new file to them: the hidden state follows the block.
      // Only this flag is carried over — allow_print and allow_download default
      // to off, so a new file never inherits a permission it was not given.
      if (contentBlock && !isVisibleToStudents(replacedBlock)) {
        contentBlock.payload = {
          ...(contentBlock.payload || {}),
          viewer: { ...(contentBlock.payload?.viewer || {}), visible_to_students: false },
        };
      }

      // Chỉ đổi phần file: khối trong bài gộp còn mang appearance (nền, padding),
      // layout và timing mà ContentBlock dựng theo — thay nguyên khối sẽ xoá hết
      // và mất luôn trên máy chủ.
      const updatedBlocks = mergedBlocks
        ? mergedBlocks.map((b: any, i: number) =>
            i === blockIndex ? { ...b, ...contentBlock } : b
          )
        : null;

      // Attach the content to the section via API
      const response = await BookService.updateSection(
        sectionId,
        updatedBlocks
          ? {
              book_id: bookId,
              parent_section_id: parentSectionId,
              content_blocks: updatedBlocks,
            }
          : {
              book_id: bookId, // Include book_id to maintain association
              parent_section_id: parentSectionId, // Include correct parent_section_id to maintain hierarchy
              content_block: contentBlock,
              // Keep legacy content for backward compatibility
              content: {
                file: {
                  id: fileData._id,
                  name: fileData.type === "url" ? "SCORM URL" : fileData.original_name,
                  type: contentType,
                  size: fileData.type === "url" ? 0 : parseInt(fileData.size),
                  url: fileUrl,
                  file_path: getFilePathFromUrl(fileUrl),
                },
              },
            }
      );

      if (response.success) {
        // Update local state instead of reloading the entire page
        setBookData((prevBook) => {
          const updatedBook = { ...prevBook };
          
          // Find and update the section with the new content_block
          const updateSectionInChapter = (sections: any[], targetSectionId: string, contentBlock: any): boolean => {
            for (const section of sections) {
              if (section.id === targetSectionId) {
                if (updatedBlocks) {
                  section.content_blocks = updatedBlocks;
                } else {
                  section.content_block = contentBlock;
                }
                return true;
              }
              // Check nested sections (for container sections)
              if (section.children && section.children.length > 0) {
                if (updateSectionInChapter(section.children, targetSectionId, contentBlock)) {
                  return true;
                }
              }
            }
            return false;
          };

          for (const chapter of updatedBook.contents || []) {
            if (chapter.children && updateSectionInChapter(chapter.children, sectionId, contentBlock)) {
              break;
            }
          }
          
          return updatedBook;
        });
      } else {
        throw new Error(
          response.message || "Failed to attach content to section"
        );
      }
    } catch (error) {
      console.error("Failed to upload/add content:", error);
      toast.error("Có lỗi xảy ra khi thêm nội dung!");
    }
  };

  // Handler for content deletion from sections (supports both file and URL removal)
  const handleContentDelete = async (sectionId: string) => {
    try {
      // Find the chapter that contains this section (including nested sections)
      let chapterId = "";
      const findSectionInChapter = (sections: any[], targetSectionId: string): boolean => {
        for (const section of sections) {
          if (section.id === targetSectionId) {
            return true;
          }
          // Check nested sections (for container sections)
          if (section.children && section.children.length > 0) {
            if (findSectionInChapter(section.children, targetSectionId)) {
              return true;
            }
          }
        }
        return false;
      };

      for (const chapter of bookData.contents || []) {
        if (chapter.children && findSectionInChapter(chapter.children, sectionId)) {
          chapterId = chapter.id || ""; // Use empty string if id is not available
          break;
        }
      }

      if (!chapterId) {
        throw new Error("Chapter not found for section");
      }

      // Remove content from section via API
      const response = await BookService.removeFileFromSection(
        sectionId
      );

      if (response.success) {
        // Update local state (handle nested sections)
        setBookData((prevBook) => {
          const updatedBook = { ...prevBook };

          const clearSectionContent = (sections: any[]): boolean => {
            for (const section of sections) {
              if (section.id === sectionId) {
                // Clear section content information
                section.content = {};
                section.content_block = undefined;
                return true;
              }
              // Check nested sections (for container sections)
              if (section.children && section.children.length > 0) {
                if (clearSectionContent(section.children)) {
                  return true;
                }
              }
            }
            return false;
          };

          for (const chapter of updatedBook.contents || []) {
            if (chapter.children && clearSectionContent(chapter.children)) {
              break;
            }
          }

          return updatedBook;
        });


      } else {
        throw new Error(
          response.message || "Failed to remove content from section"
        );
      }
    } catch (error) {
      console.error("Failed to remove content:", error);
      toast.error("Có lỗi xảy ra khi xóa nội dung!");
    }
  };

  // Handler for adding question to quiz
  const handleAddQuestion = (sectionId: string) => {
    setCurrentQuizSectionId(sectionId);
    setEditingQuestion(null);
    setIsQuestionModalOpen(true);
  };


  // Handler for saving question inline
  const handleSaveQuestionInline = async (sectionId: string, question: Question) => {
    try {
      // Find the section and update its questions
      const updatedBookData = { ...bookData };
      
      // Find the chapter containing the section
      const chapterIndex = updatedBookData.contents?.findIndex(chapter => 
        chapter.children?.some(section => section.id === sectionId)
      );
      
      if (chapterIndex !== undefined && chapterIndex >= 0 && updatedBookData.contents) {
        const chapter = updatedBookData.contents[chapterIndex];
        const sectionIndex = chapter.children?.findIndex(section => section.id === sectionId);
        
        if (sectionIndex !== undefined && sectionIndex >= 0 && chapter.children) {
          const section = chapter.children[sectionIndex];
          
          // Initialize content_block if it doesn't exist
          if (!section.content_block) {
            section.content_block = {
              content_type: 'quiz',
              payload: {
                questions: [],
                time_limit_minutes: 30,
                passing_score: 70,
                allow_retake: true,
                show_correct_answers: true
              }
            };
          }
          
          // Initialize questions array if it doesn't exist
          if (!section.content_block.payload.questions) {
            section.content_block.payload.questions = [];
          }
          
          // Check if this is a new question or updating existing one
          const existingQuestionIndex = section.content_block.payload.questions.findIndex(
            (q: any) => q.question_id === question.question_id
          );
          
          if (existingQuestionIndex >= 0) {
            // Update existing question
            section.content_block.payload.questions[existingQuestionIndex] = question;
          } else {
            // Add new question
            section.content_block.payload.questions.push(question);
          }
          
          // Update the book data
          setBookData(updatedBookData);
          
          // Save to API
          await BookService.updateSection(sectionId, {
            content_block: section.content_block
          });
          
          console.log("Question saved successfully");
        }
      }
    } catch (error) {
      console.error("Failed to save question:", error);
      toast.error("Có lỗi xảy ra khi lưu câu hỏi!");
    }
  };

  // Handler for editing question in quiz
  const handleEditQuestion = (sectionId: string, question: Question) => {
    // This will be handled by module-card for inline editing
    // No need to open modal anymore
  };

  // Handler for saving question to quiz
  const handleSaveQuestion = async (question: Question) => {
    if (!currentQuizSectionId) return;

    try {
      // Find the chapter and section containing this quiz
      let chapterId = "";
      let targetSection = null;
      
      for (const chapter of bookData.contents || []) {
        for (const section of chapter.children || []) {
          if (section.id === currentQuizSectionId) {
            chapterId = chapter.id || "";
            targetSection = section;
            break;
          }
        }
        if (targetSection) break;
      }

      if (!targetSection || !chapterId) {
        throw new Error("Quiz section not found");
      }

      // Update the quiz content_block with the new question
      const updatedContentBlock = {
        content_type: "quiz" as const,
        payload: {
          ...targetSection.content_block?.payload,
          questions: editingQuestion 
            ? (targetSection.content_block?.payload?.questions || []).map((q: Question) => 
                q.question_id === question.question_id ? question : q
              )
            : [...(targetSection.content_block?.payload?.questions || []), question]
        }
      };

      // Update section via API
      const response = await BookService.updateSection(
        currentQuizSectionId,
        {
          book_id: bookId,
          parent_section_id: chapterId,
          content_block: updatedContentBlock,
        }
      );

      if (response.success) {
        // Update local state
        setBookData((prevBook) => {
          const updatedBook = { ...prevBook };
          
          for (const chapter of updatedBook.contents || []) {
            if (chapter.id === chapterId) {
              for (const section of chapter.children || []) {
                if (section.id === currentQuizSectionId) {
                  section.content_block = updatedContentBlock;
                  break;
                }
              }
              break;
            }
          }
          
          return updatedBook;
        });
        
        toast.success(editingQuestion ? "Câu hỏi đã được cập nhật!" : "Câu hỏi đã được thêm!");
      } else {
        throw new Error(response.message || "Failed to save question");
      }
    } catch (error) {
      console.error("Failed to save question:", error);
      toast.error("Có lỗi xảy ra khi lưu câu hỏi!");
    }
  };

  // Per-block viewer switches (allow_print, allow_download, visible_to_students):
  // flips payload.viewer[option] on one block (single content_block, or
  // content_blocks[blockIndex] for merged sections) and persists just that
  // field set.
  //
  // Áp vào state trước rồi mới gọi API, và đọc state qua ref: ba công tắc nay
  // nằm cạnh nhau trong một header nên bấm liên tiếp là chuyện thường. Nếu đợi
  // server mới đổi state thì ô tick không nhúc nhích, người dùng bấm tiếp ô thứ
  // hai, handler đó đọc lại đúng bản cũ và ghi đè mất lựa chọn thứ nhất.
  const handleSetViewerOption = async (
    sectionId: string,
    blockIndex: number | undefined,
    option: ViewerOption,
    value: boolean
  ) => {
    const withOption = (block: any) => ({
      ...block,
      payload: {
        ...(block?.payload || {}),
        viewer: { ...(block?.payload?.viewer || {}), [option]: value },
      },
    });

    const target = findSectionById(bookDataRef.current.contents || [], sectionId);
    if (!target) {
      console.error(`Failed to update viewer option ${option}: section not found`);
      toast.error(VIEWER_OPTION_ERRORS[option]);
      return;
    }

    let patch: Record<string, any>;
    let rollback: Record<string, any>;
    if (blockIndex !== undefined && Array.isArray(target.content_blocks)) {
      rollback = { content_blocks: target.content_blocks };
      patch = {
        content_blocks: target.content_blocks.map((block: any, i: number) =>
          i === blockIndex ? withOption(block) : block
        ),
      };
    } else if (target.content_block) {
      rollback = { content_block: target.content_block };
      patch = { content_block: withOption(target.content_block) };
    } else {
      console.error(`Failed to update viewer option ${option}: section has no block`);
      toast.error(VIEWER_OPTION_ERRORS[option]);
      return;
    }

    // Ghi thẳng vào chính object mà bookData đang giữ (như các handler khác
    // trong file), rồi ép dựng lại để ô tick đổi ngay.
    const applyToState = (fields: Record<string, any>) => {
      Object.assign(target, fields);
      setBookData((prevBook) => ({ ...prevBook }));
    };

    applyToState(patch);

    try {
      const response = await BookService.updateSection(sectionId, { book_id: bookId, ...patch });
      if (!response.success) {
        throw new Error(response.message || "Failed to update viewer option");
      }
    } catch (error) {
      console.error(`Failed to update viewer option ${option}:`, error);
      applyToState(rollback);
      toast.error(VIEWER_OPTION_ERRORS[option]);
    }
  };

  // Bật/tắt cả một chương hoặc tiết với người đọc. Ghi ngay như các công tắc
  // của khối, đổi trên giao diện trước rồi trả lại nếu API báo lỗi.
  const handleSetReaderVisible: SetReaderVisible = async (sectionId, visible) => {
    const target = findSectionById(bookDataRef.current.contents || [], sectionId);
    if (!target) {
      console.error("Failed to toggle reader visibility: section not found");
      toast.error("Có lỗi xảy ra khi bật/tắt phần này!");
      return;
    }

    const previous = target.visible_to_readers;
    const applyToState = (value: boolean | undefined) => {
      target.visible_to_readers = value;
      setBookData((prevBook) => ({ ...prevBook }));
    };

    applyToState(visible);

    try {
      const response = await BookService.updateSection(sectionId, {
        book_id: bookId,
        visible_to_readers: visible,
      });
      if (!response.success) {
        throw new Error(response.message || "Failed to toggle reader visibility");
      }
    } catch (error) {
      console.error("Failed to toggle reader visibility:", error);
      applyToState(previous);
      toast.error("Có lỗi xảy ra khi bật/tắt phần này!");
    }
  };

  // Handler for updating quiz settings
  const handleUpdateQuizSettings = async (sectionId: string, settings: any) => {
    try {
      // Find the chapter and section containing this quiz
      let chapterId = "";
      let targetSection = null;
      
      for (const chapter of bookData.contents || []) {
        for (const section of chapter.children || []) {
          if (section.id === sectionId) {
            chapterId = chapter.id || "";
            targetSection = section;
            break;
          }
        }
        if (targetSection) break;
      }

      if (!targetSection || !chapterId) {
        throw new Error("Quiz section not found");
      }

      // Update the quiz content_block with new settings
      const updatedContentBlock = {
        content_type: "quiz" as const,
        payload: {
          ...targetSection.content_block?.payload,
          ...settings
        }
      };

      // Update section via API
      const response = await BookService.updateSection(
        sectionId,
        {
          book_id: bookId,
          parent_section_id: chapterId,
          content_block: updatedContentBlock,
        }
      );

      if (response.success) {
        // Update local state
        setBookData((prevBook) => {
          const updatedBook = { ...prevBook };
          
          for (const chapter of updatedBook.contents || []) {
            if (chapter.id === chapterId) {
              for (const section of chapter.children || []) {
                if (section.id === sectionId) {
                  section.content_block = updatedContentBlock;
                  break;
                }
              }
              break;
            }
          }
          
          return updatedBook;
        });
        
        toast.success("Cài đặt quiz đã được cập nhật!");
      } else {
        throw new Error(response.message || "Failed to update quiz settings");
      }
    } catch (error) {
      console.error("Failed to update quiz settings:", error);
      toast.error("Có lỗi xảy ra khi cập nhật cài đặt quiz!");
    }
  };

  // Handler for deleting question from quiz
  const handleDeleteQuestion = async (sectionId: string, questionId: string) => {
    try {
      // Find the chapter and section containing this quiz
      let chapterId = "";
      let targetSection = null;
      
      for (const chapter of bookData.contents || []) {
        for (const section of chapter.children || []) {
          if (section.id === sectionId) {
            chapterId = chapter.id || "";
            targetSection = section;
            break;
          }
        }
        if (targetSection) break;
      }

      if (!targetSection || !chapterId) {
        throw new Error("Quiz section not found");
      }

      // Remove question from quiz content_block
      const updatedContentBlock = {
        content_type: "quiz" as const,
        payload: {
          ...targetSection.content_block?.payload,
          questions: (targetSection.content_block?.payload?.questions || []).filter(
            (q: Question) => q.question_id !== questionId
          )
        }
      };

      // Update section via API
      const response = await BookService.updateSection(
        sectionId,
        {
          book_id: bookId,
          parent_section_id: chapterId,
          content_block: updatedContentBlock,
        }
      );

      if (response.success) {
        // Update local state
        setBookData((prevBook) => {
          const updatedBook = { ...prevBook };
          
          for (const chapter of updatedBook.contents || []) {
            if (chapter.id === chapterId) {
              for (const section of chapter.children || []) {
                if (section.id === sectionId) {
                  section.content_block = updatedContentBlock;
                  break;
                }
              }
              break;
            }
          }
          
          return updatedBook;
        });
        
        toast.success("Câu hỏi đã được xóa!");
      } else {
        throw new Error(response.message || "Failed to delete question");
      }
    } catch (error) {
      console.error("Failed to delete question:", error);
      toast.error("Có lỗi xảy ra khi xóa câu hỏi!");
    }
  };

  // Handler for saving book changes
  const handleSaveBook = async () => {
    try {
      // Save book changes via API
      const response = await BookService.updateBook(bookId, {
        title: bookData.title,
        description: bookData.description,
        category: bookData.category,
        status: bookData.status,
      });
      
      if (response.success) {

        toast.success("Sách đã được lưu thành công!");
      } else {
        throw new Error(response.message || "Failed to save book");
      }
    } catch (error) {
      console.error("Failed to save book:", error);
      toast.error("Có lỗi xảy ra khi lưu sách!");
    }
  };

  // Handler for import button
  const handleImport = (type: 'json' | 'dlow') => {
    setImportType(type);
    setIsImportModalOpen(true);
  };

  // Handler for import success
  const handleImportSuccess = () => {
    // Reload book data after successful import
    reloadBookData();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Đang tải thông tin sách...</div>
      </div>
    );
  }

  return (
    <div className="mx-auto">
      {/* Add CSS for highlight effect */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .section-highlight {
            animation: highlightPulse 2s ease-in-out !important;
            border: 2px solid #3b82f6 !important;
            box-shadow: 0 0 20px rgba(59, 130, 246, 0.3) !important;
            border-radius: 8px !important;
          }
          
          @keyframes highlightPulse {
            0% {
              box-shadow: 0 0 0 rgba(59, 130, 246, 0.7);
            }
            50% {
              box-shadow: 0 0 20px rgba(59, 130, 246, 0.7);
            }
            100% {
              box-shadow: 0 0 0 rgba(59, 130, 246, 0.7);
            }
          }
        `
      }} />
      {/* Question modal */}
      <QuestionModal
        isOpen={isQuestionModalOpen}
        onClose={() => {
          setIsQuestionModalOpen(false);
          setEditingQuestion(null);
          setCurrentQuizSectionId(null);
        }}
        onSave={handleSaveQuestion}
        editingQuestion={editingQuestion}
        translations={{
          editQuestion: t("course-content.composer.editQuestion"),
          addQuestion: t("course-content.composer.addNewQuestion"),
          questionText: t("course-content.composer.questionText"),
          questionType: t("course-content.composer.questionType"),
          answerOptions: t("course-content.composer.answerOptions"),
          correctAnswer: t("course-content.composer.correctAnswer"),
          difficulty: t("course-content.composer.difficulty"),
          tags: t("course-content.composer.tags"),
          cancel: t("course-content.composer.cancel"),
          saveChanges: t("course-content.composer.saveChanges"),
          addOption: t("course-content.composer.addOption"),
        }}
      />
      
      {/* Content Type Selection Modal */}
      <ContentTypeModal
        isOpen={isContentTypeModalOpen}
        onClose={() => {
          setIsContentTypeModalOpen(false);
          setPendingChapterId(null);
          setPendingContainerId(null);
        }}
        onSelect={handleContentTypeSelect}
      />
      
      {/* Book-specific modals can be added here in the future */}

      {/* Book Header */}
      <BookHeader
        bookTitle={bookData.title || ""}
        bookStatus={bookData.status || ""}
        bookData={transformToPreviewData(bookData)}
        onSave={handleSaveBook}
        onImport={handleImport}
        t={t}
      />

      {/* Import Modal */}
      <BookImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImportSuccess={handleImportSuccess}
        importType={importType}
        bookId={bookId}
      />

      {/* Content with top padding to account for fixed header */}
      <div className="mt-4">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Left sidebar - Book Navigation */}
          <BookNavigation 
            contents={bookData.contents || []} 
            t={t} 
            onItemSelect={handleItemSelect}
          />

          {/* Right area - Content Editor */}
          <div className="md:w-3/4 space-y-6 max-h-[calc(100vh-12rem)] overflow-y-auto pr-4">
            {/* Content Sections */}
            {(bookData.contents || []).length === 0 ? (
              <div className="text-center py-8">
                <div className="text-gray-500 dark:text-gray-400">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mx-auto mb-4"
                  >
                    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                    Chưa có chương nào
                  </h3>
                  <p className="text-sm">
                    Sách này chưa có chương nào. Bấm chỉnh sửa để bắt đầu.
                  </p>
                </div>
              </div>
            ) : (
              (bookData.contents || []).map((chapter: Chapter, chapterIndex: number) => {
                // Create adapters for ModuleCard compatibility
                const moduleAdapter = {
                  id: chapter.id,
                  title: chapter.title,
                  type: (chapter.type as any) || ("combined" as const),
                  duration: 0,
                  start_day_offset: 0,
                  start_day_offset_relative_to: "course" as const,
                  order: chapter.order,
                  sections: (chapter.children || []).map((section) => ({
                    id: section.id,
                    title: section.title,
                    order: section.order,
                    delivery_method: "combined" as const,
                    start_day_offset: 0,
                    start_day_offset_relative_to: "module" as const,
                    activities: [],
                  })),
                  content_block: chapter.content_block,
                };

                const bookModuleAdapter = {
                  id: chapter.id,
                  title: chapter.title,
                  type: (chapter.type as any) || ("combined" as const),
                  duration: chapter.duration || 0,
                  start_day_offset: 0,
                  start_day_offset_relative_to: "course" as const,
                  order: chapter.order,
                  content_block: chapter.content_block,
                  visible_to_readers: chapter.visible_to_readers,
                  sections: (chapter.children || []).map((section, sectionIndex) => {
                    // Skip file extraction if section has content_blocks (merged blocks)
                    // Extract file information from content_block only if no content_blocks
                    let fileInfo = null;
                    if (!section.content_blocks && section.content_block) {
                      const { content_type, payload } = section.content_block;
                      switch (content_type) {
                        case "pdf":
                          fileInfo = {
                            file_id: section.id,
                            file_name: section.title,
                            file_type: "pdf",
                            file_size: 0,
                            file_url: payload.file_url || "",
                          };
                          break;
                        case "scorm":
                        case "html_package":
                          fileInfo = {
                            file_id: section.id,
                            file_name: section.title,
                            file_type: content_type,
                            file_size: 0,
                            file_url: payload.file_url || "",
                          };
                          break;
                        case "pptx":
                          fileInfo = {
                            file_id: section.id,
                            file_name: section.title,
                            file_type: "pptx",
                            file_size: 0,
                            file_url: payload.file_url || "",
                          };
                          break;
                        case "video":
                          fileInfo = {
                            file_id: section.id,
                            file_name: section.title,
                            file_type: "video",
                            file_size: 0,
                            file_url: payload.file_url || "",
                          };
                          break;
                        case "audio":
                          fileInfo = {
                            file_id: section.id,
                            file_name: section.title,
                            file_type: "audio",
                            file_size: 0,
                            file_url: payload.file_url || "",
                          };
                          break;
                        case "embed":
                          fileInfo = {
                            file_id: section.id,
                            file_name: section.title,
                            file_type: "url",
                            file_size: 0,
                            file_url: payload.url || "",
                          };
                          break;
                        default:
                          // For html, media, quiz content types
                          fileInfo = {
                            file_id: section.id,
                            file_name: section.title,
                            file_type: content_type,
                            file_size: 0,
                            file_url: "",
                          };
                      }
                    }

                    const sectionAdapter = {
                      id: section.id,
                      title: section.title,
                      order: section.order,
                      duration: section.duration || 0,
                      type: section.type,
                      start_day_offset: 0,
                      start_day_offset_relative_to: "module" as const,
                      // Don't set file fields if content_blocks exists
                      file_id: section.content_blocks ? undefined : (fileInfo?.file_id || ""),
                      file: section.content_blocks ? undefined : (fileInfo || {
                        file_id: "",
                        file_name: "",
                        file_size: 0,
                        file_type: "",
                        file_url: "",
                      }),
                      content: section.content_blocks ? undefined : (fileInfo
                        ? {
                            file: {
                              id: fileInfo.file_id,
                              name: fileInfo.file_name,
                              type: fileInfo.file_type,
                              size: fileInfo.file_size,
                              url: fileInfo.file_url,
                            },
                          }
                        : undefined),
                      content_block: section.content_block,
                      content_blocks: section.content_blocks,
                      access: section.access,
                      visible_to_readers: section.visible_to_readers,
                      progress_rules: section.progress_rules,
                      estimates: section.estimates,
                      children: section.children,
                    };

                    return sectionAdapter;
                  }),
                };

                return (
                  <ModuleCard
                    key={chapter.id || `chapter-${chapterIndex}`}
                    module={moduleAdapter}
                    bookModule={bookModuleAdapter}
                    moduleIndex={chapterIndex}
                    onContentUpload={handleContentUpload}
                    onContentDelete={handleContentDelete}
                    onSetViewerOption={handleSetViewerOption}
                    onSetReaderVisible={handleSetReaderVisible}
                    onAddSection={() => {
                      if (chapter.id) {
                        handleAddSection(chapter.id);
                      } else {
                        console.error("❌ Chapter ID is invalid for adding section:", chapter);
                        toast.error("Không thể thêm phần: ID chương không hợp lệ!");
                      }
                    }}
                    onAddSubSection={handleAddSubSection}
                    isAddingSection={isAddingSection === chapter.id}
                    isAddingSubSection={isAddingSection}
                    onUpdateModule={(updatedModule) => {
                      if (chapter.id) {
                        // Convert to Chapter format
                        const updatedChapter: Partial<Chapter> = {
                          id: updatedModule.id,
                          title: updatedModule.title,
                          order: chapterIndex + 1,
                        };
                        handleUpdateChapter(chapter.id, updatedChapter);
                      } else {
                        console.error("❌ Chapter ID is invalid for updating:", chapter);
                        toast.error("Không thể cập nhật chương: ID không hợp lệ!");
                      }
                    }}
                    onUpdateSection={async (sectionId, updatedSection) => {
                      if (chapter.id && sectionId) {
                        // Find the original section to preserve its type and determine parent
                        let originalType = "text"; // Default fallback
                        let parentSectionId = chapter.id; // Default to chapter ID
                        
                        const findSectionInfo = (sections: any[], parentId: string): { type: string | null; parentId: string } | null => {
                          for (const section of sections) {
                            if (section.id === sectionId) {
                              return { type: section.type, parentId };
                            }
                            // Check nested sections (for container sections)
                            if (section.children && section.children.length > 0) {
                              const result = findSectionInfo(section.children, section.id);
                              if (result) return result;
                            }
                          }
                          return null;
                        };
                        
                        const sectionInfo = findSectionInfo(chapter.children || [], chapter.id);
                        if (sectionInfo) {
                          originalType = sectionInfo.type || "text";
                          parentSectionId = sectionInfo.parentId;
                        }

                        // Convert to Section format. Drop undefined keys so a
                        // partial update (title only) does not blank out the
                        // other fields in local state.
                        const bookSection = Object.fromEntries(
                          Object.entries({
                            id: updatedSection.id,
                            title: updatedSection.title,
                            order: updatedSection.order,
                            type: originalType as any, // Preserve original type instead of defaulting to text
                          }).filter(([, value]) => value !== undefined)
                        ) as Partial<Section>;
                        return await handleUpdateSection(parentSectionId, sectionId, bookSection);
                      } else {
                        console.error("❌ Invalid IDs for updating section:", {
                          chapterId: chapter.id,
                          sectionId,
                        });
                        toast.error("Không thể cập nhật phần: ID không hợp lệ!");
                        throw new Error("Invalid IDs for updating section");
                      }
                    }}
                    onDeleteModule={() => {
                      if (chapter.id) {
                        handleDeleteChapter(chapter.id);
                      } else {
                        console.error("❌ Chapter ID is missing or invalid:", chapter);
                        console.error("Chapter object keys:", Object.keys(chapter));
                        console.error("Chapter object values:", Object.values(chapter));
                        toast.error("Không thể xóa chương: ID không hợp lệ!");
                      }
                    }}
                    onDeleteSection={(sectionId) => {
                      if (sectionId && chapter.id) {
                        handleDeleteSection(chapter.id, sectionId);
                      } else {
                        console.error("❌ Invalid IDs for section deletion:");
                        console.error("Section ID:", sectionId);
                        console.error("Chapter ID:", chapter.id);
                        console.error("Chapter object:", chapter);
                        toast.error("Không thể xóa phần: ID không hợp lệ!");
                      }
                    }}
                    onAddQuestion={handleAddQuestion}
                    onEditQuestion={handleEditQuestion}
                    onSaveQuestion={handleSaveQuestionInline}
                    onDeleteQuestion={handleDeleteQuestion}
                    onUpdateQuizSettings={handleUpdateQuizSettings}
                    t={t}
                  />
                );
              })
            )}

            {/* Add New Chapter Button */}
            <div className="flex justify-center gap-4">
              <Button variant="outline" onClick={handleAddChapter}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mr-2"
                >
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                Thêm chương
              </Button>

              <Button variant="outline" onClick={reloadBookData} disabled={loading}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={`mr-2 ${loading ? "animate-spin" : ""}`}
                >
                  <path d="M21 2v6h-6"></path>
                  <path d="M3 12a9 9 0 0 1 15-6.7L21 8"></path>
                  <path d="M3 22v-6h6"></path>
                  <path d="M21 12a9 9 0 0 1-15 6.7L3 16"></path>
                </svg>
                {loading ? "Đang tải..." : "Làm mới dữ liệu"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
